import axios from 'axios'

import { LOCALSTORAGE_TOKENS, ROOT_URL } from '../constants'
import * as Sentry from '@sentry/browser'

import firebase from './firebase'
import { FEATURE_FLAGS, isFeatureEnabled } from './featureFlags'
import { api as guarantorApi } from './guarantor.api'
import { toast } from 'react-toastify'
import { interceptApi } from '../utils/interceptApi'
import { getSlugPartner } from '../utils/getSlugPartner'
import { history } from '../navigation/history'

const api = axios.create({
  baseURL: ROOT_URL.credithpath_isa_allowance,
})

const userApi = axios.create({
  baseURL: ROOT_URL.user,
})

interceptApi(api)
interceptApi(userApi)

const partner = getSlugPartner()

export const getCreditPath = (partnerSlug, screenUniqueName) => {
  return new Promise(async (resolve, reject) => {
    const routeUniqueName = `/${screenUniqueName}`
    try {
      const { data } = await api.get(`/credit-paths/${partnerSlug}${screenUniqueName ? routeUniqueName : ''}`)

      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'errorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning,
      })
      reject(err)
    }
  })
}

export const postCreditPath = (body, partnerSlug, endpoint = null) => {
  return new Promise(async (resolve, reject) => {
    try {
      const fullPath = endpoint || `/credit-paths/${partnerSlug}`
      const response = await api.post(fullPath, body)
      resolve(response)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'errorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning,
      })
      reject(err)
    }
  })
}

export const PhoneResendRewrite = (phone, partnerSlug) => {
  const body = {
    phone: phone,
  }
  return new Promise(async (resolve, reject) => {
    try {
      const response = await api.post(`/rewrite-resend/${partnerSlug}`, body)
      resolve(response)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'errorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning,
      })
      reject(err)
    }
  })
}

export const validateToken = (token) => {
  return new Promise(async (resolve, reject) => {
    try {
      api.defaults.headers.Authorization = token
      const response = await api.get('/auth')
      resolve(response)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'errorRequestAuth',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning,
      })
      reject(err)
    }
  })
}

export const getAddressFromCep = async (zipCode) => {
  const cleanZipCode = zipCode.replace(/[^\d]+/g, '')

  try {
    const { data } = await axios.get(`https://viacep.com.br/ws/${cleanZipCode}/json/`)

    if (data?.erro) {
      throw new Error('CEP não encontrado.')
    }

    return data
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'errorCEP',
      message: 'ERROR INFO ' + err,
      level: Sentry.Severity.Warning,
    })
  }
}

/**
 * Asks the backend for a presigned POST and uploads the file straight to S3.
 * Students get it from ms_credit_path_isa_allowance and guarantors from
 * ms_guarantor. Returns the key, sent instead of the URL.
 * @param {File|Blob} file
 * @param {object} options
 * @param {string} options.documentType
 * @param {boolean} options.isGuarantor
 * @returns {Promise<{ key: string, typeExtension: string }>}
 */
const uploadDocument = async (file, { documentType, isGuarantor }) => {
  const body = { documentType, contentType: file.type || 'application/octet-stream' }

  const uploadUrl = isGuarantor
    ? (await guarantorApi.post('/document-upload', body)).data.data
    : (await api.post(`/credit-paths/${getSlugPartner()}/document-upload`, body)).data

  // The signed fields must come before the file, as S3 requires.
  const form = new FormData()
  Object.entries(uploadUrl.fields).forEach(([name, value]) => form.append(name, value))
  form.append('file', file)

  await axios.post(uploadUrl.url, form)

  return { key: uploadUrl.key, typeExtension: uploadUrl.fields['Content-Type'] }
}

/**
 * Legacy upload to Firebase Storage, used while the private S3 flag is off.
 * @param {File|Blob} file
 * @returns {Promise<{ url: string, typeExtension: string }>}
 */
const uploadToFirebase = async (file) => {
  const crid = localStorage.getItem(LOCALSTORAGE_TOKENS.crid)
  const now = new Date().getTime()

  const storagePath = `/${crid}/${now}`
  const uploadRef = firebase.storage().ref(storagePath)
  await uploadRef.put(file)

  return { url: await uploadRef.getDownloadURL(), typeExtension: file.type }
}

/**
 * @param {Array<File|Blob>} array
 * @param {object} options
 * @param {string} options.documentType 'personal-document' | 'selfie' | 'proof-of-enrollment' | 'school-record'
 * @param {boolean} [options.isGuarantor]
 * @returns {Promise<Array<{ key: string, typeExtension: string } | { url: string, typeExtension: string }>>}
 */
export const uploadImages = async (array, { documentType, isGuarantor = false }) => {
  let result = []
  if (array.length) {
    try {
      const usePrivateS3 = await isFeatureEnabled(FEATURE_FLAGS.privateS3Documents)
      if (!usePrivateS3) await firebase.auth().signInAnonymously()

      for (const item of array) {
        result.push(
          usePrivateS3 ? await uploadDocument(item, { documentType, isGuarantor }) : await uploadToFirebase(item)
        )
      }
    } catch (err) {
      Sentry.captureException(err)

      toast.error('Não foi possivel enviar uma de suas imagens')
    }
  }
  return result
}

export const generateSignToken = (CreditRequestId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await userApi.post(`/provi-sign/${CreditRequestId}/generate/token`)

      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'errorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning,
      })
      reject(err)
    }
  })
}

export const getCpfInfo = async () => {
  try {
    const { data } = await api.get(`/cpf/${partner}`)

    return data
  } catch (err) {
    if (err.response?.status === 401) history.push(`/${partner}/token-invalido`)
  }
}

export const sendCpfInfo = async (data) => {
  try {
    const response = await api.post(`/cpf/${partner}`, {
      data,
    })

    return response?.data
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'errorRequest',
      message: 'ERROR INFO ' + err,
      level: Sentry.Severity.Warning,
    })
  }
}
